<template>
  <div class="contractor-agreement-list-page">
    <div class="top-bar">
      <PageTitle hideMarginLeft>Список договоров поставок</PageTitle>
      <CustomButton
        v-if="activeTab === 'agreements'"
        class="header__create-btn"
        @click="openDialog()"
      >
        <span>Добавить договор</span>
        <q-icon name="add" class="header__create-btn-icon" size="16px" />
      </CustomButton>
    </div>

    <CustomTabs
        :tab="activeTab"
        :data="tabsData"
        label-option="label"
        name-option="name"
        alt
        @change_tab="(tab) => activeTab = tab"
        :paddingless="true"
      >
        <template v-slot:default="item">
          <div class="tw-pt-4">
            <template v-if="item.data.name === 'agreements'">
              <CustomTable 
                title="Договоры поставок" 
                :rowsData="filteredRowsData" 
                :columns="columns" 
                :loading="isLoading"
                :small="true"
                :show-search="true"
                :custom-body="true"
                :sticky-header="false"
              >
                <template #top-right>
                  <q-select
                    v-model="initiatorCompanyTypeFilter"
                    class="initiator-filter"
                    dense
                    outlined
                    emit-value
                    map-options
                    :options="initiatorCompanyTypeFilterOptions"
                    label="Инициатор"
                    clearable
                  />
                </template>
                <template #customBody="props">
                  <q-tr>
                    <custom-table-cell align="center" vertical-align="center">
                      {{ props.row.contractor_agreement_list_id }}
                    </custom-table-cell>

                    <custom-table-cell align="center" vertical-align="center">
                      <q-icon
                        :name="getInitiatorCompanyTypeMeta(props.row).icon"
                        :color="getInitiatorCompanyTypeMeta(props.row).color"
                        size="18px"
                      >
                        <q-tooltip>{{ getInitiatorCompanyTypeMeta(props.row).label }}</q-tooltip>
                      </q-icon>
                    </custom-table-cell>

                    <custom-table-cell align="left" vertical-align="center">
                      {{ props.row.contractor_name || props.row.contractor_id || '-' }}
                    </custom-table-cell>

                    <custom-table-cell align="left" vertical-align="center">
                      {{ props.row.provider_name || props.row.provider_id || '-' }}
                    </custom-table-cell>

                    <custom-table-cell align="left" vertical-align="center">
                      {{ props.row.agreement_num || '-' }}
                    </custom-table-cell>

                    <custom-table-cell align="center" vertical-align="center">
                      {{ props.row.document_date ? formatDate(props.row.document_date) : '-' }}
                    </custom-table-cell>

                    <custom-table-cell align="center" vertical-align="center">
                      {{ props.row.date_end ? formatDate(props.row.date_end) : '-' }}
                    </custom-table-cell>

                    <custom-table-cell align="center" vertical-align="center">
                      <div class="integration-status">
                        <a v-if="props.row.aws_url" :href="props.row.aws_url" target="_blank" class="status-icon">
                          <q-icon name="cloud_done" color="positive" size="20px">
                            <q-tooltip>Файл загружен в хранилище</q-tooltip>
                          </q-icon>
                        </a>
                        <q-icon v-else name="cloud_off" color="grey-5" size="20px">
                          <q-tooltip>Файл не загружен</q-tooltip>
                        </q-icon>
                        
                        <q-icon 
                          v-if="props.row.is_sent_to_1c === 1 || props.row.is_sent_to_1c === true" 
                          name="send" 
                          color="positive" 
                          size="20px"
                        >
                          <q-tooltip>Отправлено в 1С</q-tooltip>
                        </q-icon>
                        <q-icon v-else name="send" color="grey-5" size="20px">
                          <q-tooltip>Не отправлено в 1С</q-tooltip>
                        </q-icon>
                      </div>
                    </custom-table-cell>
                    <custom-table-cell align="center" vertical-align="center">
                      <!-- Если документ из BINECT, показываем только текст -->
                      <div v-if="props.row.is_from_binect" class="sign-binect-badge">
                        <q-badge color="blue-grey" :label="props.row.is_from_binect_text" />
                      </div>
                      
                      <!-- Обычное отображение статуса подписей -->
                      <div v-else class="sign-status-container">
                        <!-- Иконки статуса подписей -->
                        <div class="sign-status-icons">
                          <!-- Застройщик -->
                          <div class="sign-status-row">
                            <q-icon name="domain" size="16px" color="grey-7" />
                            <q-icon 
                              :name="getSignIcon(props.row.contractor_sign_status).icon"
                              :color="getSignIcon(props.row.contractor_sign_status).color"
                              size="16px"
                            />
                            <q-tooltip>
                              Застройщик: {{ getSideSignLabel(props.row.contractor_sign_status) }}
                              <span v-if="props.row.contractor_signed_at">
                                <br>{{ formatDateTime(props.row.contractor_signed_at) }}
                              </span>
                            </q-tooltip>
                          </div>
                          <!-- Поставщик -->
                          <div class="sign-status-row">
                            <q-icon name="local_shipping" size="16px" color="grey-7" />
                            <q-icon 
                              :name="getSignIcon(props.row.provider_sign_status).icon"
                              :color="getSignIcon(props.row.provider_sign_status).color"
                              size="16px"
                            />
                            <q-tooltip>
                              Поставщик: {{ getSideSignLabel(props.row.provider_sign_status) }}
                              <span v-if="props.row.provider_signed_at">
                                <br>{{ formatDateTime(props.row.provider_signed_at) }}
                              </span>
                            </q-tooltip>
                          </div>
                        </div>

                        <!-- Информация о следующем шаге -->
                        <div v-if="props.row.next_sign_description && !props.row.is_fully_signed" class="sign-next-step">
                          <q-icon name="info" size="14px" color="info" />
                          <span class="sign-next-step-text">{{ props.row.next_sign_description }}</span>
                        </div>
                      </div>
                    </custom-table-cell>

                    <custom-table-cell align="left" vertical-align="center">
                      {{ props.row.sbs_project_name || props.row.sbs_project_id || '-' }}
                    </custom-table-cell>

                    <custom-table-cell align="left" vertical-align="center">
                      {{ props.row.kod_bk || '-' }}
                    </custom-table-cell>

                    <custom-table-cell align="left" vertical-align="center">
                      <span v-if="props.row.error" style="color: #f44336;" :title="props.row.error">
                        {{ props.row.error.length > 30 ? props.row.error.substring(0, 30) + '...' : props.row.error }}
                      </span>
                      <span v-else>-</span>
                    </custom-table-cell>

                    <custom-table-cell align="left" vertical-align="center">
                      <span v-if="props.row.guid" :title="props.row.guid" style="max-width: 200px; overflow: hidden; text-overflow: ellipsis; display: block;">
                        {{ props.row.guid }}
                      </span>
                      <span v-else>-</span>
                    </custom-table-cell>

                    <custom-table-cell align="center" vertical-align="center">
                      <CustomDropdown
                        :loading="actionLoading[props.row.contractor_agreement_list_id]"
                        @click="(action) => handleAction(action, props.row)"
                        :options="getActionsDropdown(props.row)"
                        title="Действие"
                        :isGrouped="true"
                        :groupLabels="getGroupLabels"
                      />
                    </custom-table-cell>
                  </q-tr>
                </template>
              </CustomTable>

            </template>

            <template v-if="item.data.name === 'templates'">
              <SupplyAgreementTemplates />
            </template>

            <template v-if="item.data.name === 'materials_without_agreement'">
              <MaterialsWithoutAgreement />
            </template>
          </div>
        </template>
      </CustomTabs>

    <!-- Диалог для добавления/редактирования -->
    <q-dialog v-model="dialog" persistent>
      <q-card class="agreement-modal">
        <q-card-section class="agreement-modal__header">
          <div class="agreement-modal__title">{{ editing ? 'Редактировать договор поставки' : 'Добавить договор поставки' }}</div>
          <q-btn icon="close" flat round dense v-close-popup class="agreement-modal__close" />
        </q-card-section>

        <q-card-section class="agreement-modal__content">
          <div v-if="isLoadingOptions" class="agreement-loading">
            <q-spinner color="primary" size="50px" />
            <div class="agreement-loading__text">Загрузка данных...</div>
          </div>

          <q-form v-else @submit.prevent="save" class="agreement-form">
            <div class="agreement-form__section">
              <div class="agreement-form__item">
                <CustomSelect
                  label="Кто заказывает материал"
                  :options="formattedContractors"
                  option-label-key="contractor_name"
                  option-value-key="contractor_id"
                  :value="form.contractor_id"
                  @change="(value) => form.contractor_id = value"
                  :loading="isLoadingOptions"
                  :clearable="true"
                />
              </div>
              <div class="agreement-form__item">
                <CustomSelect
                  label="Поставщик материала"
                  :options="formattedProviders"
                  option-label-key="provider_name"
                  option-value-key="provider_id"
                  :value="form.provider_id"
                  @change="(value) => form.provider_id = value"
                  :loading="isLoadingOptions"
                  :clearable="true"
                />
              </div>
              <div class="agreement-form__item">
                <CustomDate
                  label="Дата документа"
                  placeholder="Дата документа"
                  :value="form.document_date || ''"
                  @change_date="(value) => form.document_date = value"
                  :required="true"
                />
              </div>
              <div class="agreement-form__item">
                <CustomDate
                  label="Дата окончания договора"
                  placeholder="Дата окончания договора"
                  :value="form.date_end || ''"
                  @change_date="(value) => form.date_end = value"
                />
                <div v-if="dateEndHint" class="agreement-form__hint">
                  {{ dateEndHint }}
                </div>
              </div>
              <div class="agreement-form__item">
                <CustomSelect
                  label="Проект"
                  :options="formattedSbsProjects"
                  option-label-key="sbs_project_name"
                  option-value-key="sbs_project_id"
                  :value="form.sbs_project_id"
                  @change="(value) => form.sbs_project_id = value"
                  :loading="isLoadingSbsProjects"
                  :clearable="true"
                />
              </div>

              <!-- Галочка BINECT -->
              <div class="agreement-form__item">
                <q-checkbox
                  v-model="form.is_from_binect"
                  label="Договор подписан в другой системе"
                  color="primary"
                />
              </div>

              <!-- Поля для выбора договора из SBS (если застройщик, галочка включена и is_builder_company = true) -->
              <div v-if="form.is_from_binect && selectedContractor && selectedContractor.is_builder_company && selectedProvider && selectedSbsProject" class="agreement-form__item">
                <CustomSelect
                  label="Договор из справочника SBS"
                  :options="sbsAgreements"
                  option-label-key="dogovor_name"
                  option-value-key="guid"
                  :value="form.sbs_agreement_guid"
                  @change="handleSbsAgreementChange"
                  :loading="isLoadingSbsAgreements"
                  :clearable="true"
                  :hint="isLoadingSbsAgreements ? 'Загрузка договоров из SBS...' : (sbsAgreements.length === 0 ? 'Договоры не найдены в справочнике SBS' : '')"
                />
              </div>

              <!-- Поля для ручного ввода (если галочка включена И is_builder_company = false) -->
              <div v-if="form.is_from_binect && selectedContractor && !selectedContractor.is_builder_company" class="agreement-form__binect-fields">
                <div class="agreement-form__item">
                  <q-input
                    v-model="form.agreement_num"
                    label="Номер договора"
                    outlined
                    dense
                    :rules="[val => !!val || 'Обязательное поле']"
                  />
                </div>
                <div class="agreement-form__item">
                  <q-input
                    v-model="form.guid"
                    label="GUID договора в 1С"
                    outlined
                    dense
                    :rules="[val => !!val || 'Обязательное поле']"
                  />
                </div>
              </div>
            </div>

            <div class="agreement-modal__footer">
              <CustomButton
                @click="dialog = false"
                :disabled="isSaving"
                outlined
              >
                Отмена
              </CustomButton>
              <CustomButton
                type="submit"
                :loading="isSaving"
              >
                {{ editing ? 'Сохранить' : 'Создать' }}
              </CustomButton>
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Диалог подтверждения удаления -->
    <q-dialog v-model="confirmDialog.show" persistent>
      <q-card style="min-width: 350px">
        <q-card-section>
          <div class="text-h6">{{ confirmDialog.title }}</div>
        </q-card-section>

        <q-card-section>
          {{ confirmDialog.message }}
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="confirmDialog.show = false" />
          <q-btn unelevated label="Удалить" color="negative" @click="onConfirmDelete" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import CustomDate from "@/components/form/CustomDate";
import CustomButton from "@/components/common/CustomButton.vue";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import PageTitle from "@/components/common/PageTitle.vue";
import CustomSelect from "@/components/form/CustomSelect";
import CustomDropdown from "@/components/common/CustomDropdown";
import {
  getContractorAgreementList,
  saveContractorAgreementList,
  deleteContractorAgreementList,
  contractorAgreementListColumns,
  getContractors,
  getProviders
} from "@/views/ContractorAgreementList/services";
import { contractorAgreementListAPI } from "@/views/ContractorAgreementList/services/api";
import SupplyAgreementTemplates from "@/views/ContractorAgreementList/SupplyAgreementTemplates.vue";
import MaterialsWithoutAgreement from "@/views/ContractorAgreementList/MaterialsWithoutAgreement.vue";
import CustomTabs from "@/components/common/CustomTabs";
import { supplyAgreementTemplateAPI } from "@/views/ContractorAgreementList/services/templateApi";

export default {
  name: "ContractorAgreementList",
  components: {
    CustomDate,
    CustomSelect,
    CustomDropdown,
    CustomButton,
    CustomTable,
    CustomTableCell,
    PageTitle,
    CustomTabs,
    SupplyAgreementTemplates,
    MaterialsWithoutAgreement,
  },
  data() {
    return {
      activeTab: 'agreements',
      canManageTemplates: false,
      rowsData: [],
      isLoading: false,
      initiatorCompanyTypeFilter: null,
      dialog: false,
      editing: null,
      form: {
        contractor_agreement_list_id: null,
        contractor_id: null,
        provider_id: null,
        project_id: null,
        document_date: '',
        date_end: '',
        is_from_binect: false,
        agreement_num: '',
        guid: '',
        sbs_agreement_guid: null,
        sbs_dogovor_kontragenta_id: null
      },
      isSaving: false,
      confirmDialog: {
        show: false,
        title: '',
        message: '',
        onConfirm: null
      },
      columns: contractorAgreementListColumns,
      contractors: [],
      providers: [],
      sbsProjects: [],
      sbsAgreements: [],
      isLoadingOptions: false,
      isLoadingSbsProjects: false,
      isLoadingSbsAgreements: false,
      actionLoading: {},
    };
  },
  computed: {
    tabsData() {
      const tabs = [
        { name: 'agreements', label: 'Договоры' },
        { name: 'materials_without_agreement', label: 'Материалы без договоров' },
      ];
      if (this.canManageTemplates) {
        tabs.splice(1, 0, { name: 'templates', label: 'Шаблоны поставщиков' });
      }
      return tabs;
    },
    initiatorCompanyTypeFilterOptions() {
      return [
        { label: 'Застройщик', value: 'is_company' },
        { label: 'Подрядчик', value: 'is_partner' },
        { label: 'Не определено', value: 'is_no' },
      ]
    },
    filteredRowsData() {
      const type = this.initiatorCompanyTypeFilter
      if (!type) return this.rowsData
      return (this.rowsData || []).filter((t) => {
        const rowType = t?.initiator_company_type || 'is_no'
        return rowType === type
      })
    },
    formattedContractors() {
      return this.contractors.map(item => ({
        ...item,
        contractor_name: item.contractor_id ? `${item.contractor_id} / ${item.contractor_name || ''}` : item.contractor_name
      }));
    },
    formattedProviders() {
      return this.providers.map(item => ({
        ...item,
        provider_name: item.provider_id ? `${item.provider_id} / ${item.provider_name || ''}` : item.provider_name
      }));
    },
    formattedSbsProjects() {
      return this.sbsProjects.map(item => ({
        ...item,
        sbs_project_name: item.sbs_project_id ? `${item.sbs_project_id} / ${item.sbs_project_name || ''}` : item.sbs_project_name
      }));
    },
    selectedContractor() {
      if (!this.form.contractor_id) return null;
      return this.contractors.find(c => c.contractor_id === this.form.contractor_id);
    },
    selectedProvider() {
      if (!this.form.provider_id) return null;
      return this.providers.find(p => p.provider_id === this.form.provider_id);
    },
    selectedSbsProject() {
      if (!this.form.sbs_project_id) return null;
      return this.sbsProjects.find(p => p.sbs_project_id === this.form.sbs_project_id);
    },
    dateEndHint() {
      if (this.form.date_end) return '';
      const defaultEnd = this.getDefaultDateEndFromDocumentDate(this.form.document_date);
      if (!defaultEnd) return 'Если не указать, будет установлено: дата документа + 1 год';
      return `Если не указать, будет установлено: ${defaultEnd} (дата документа + 1 год)`;
    },
  },
  methods: {
    getDefaultDateEndFromDocumentDate(documentDate) {
      if (!documentDate) return '';
      // Ожидаем DD.MM.YYYY (как отдаёт CustomDate)
      const match = String(documentDate).match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      if (!match) return '';
      const day = Number(match[1]);
      const month = Number(match[2]);
      const year = Number(match[3]);
      if (!day || !month || !year) return '';

      const dt = new Date(year, month - 1, day);
      if (isNaN(dt.getTime())) return '';
      dt.setFullYear(dt.getFullYear() + 1);

      const d = String(dt.getDate()).padStart(2, '0');
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const y = dt.getFullYear();
      return `${d}.${m}.${y}`;
    },
    getInitiatorCompanyTypeMeta(row) {
      const type = row?.initiator_company_type || 'is_no'
      if (type === 'is_company') {
        return { icon: 'domain', color: 'primary', label: 'Инициатор: застройщик' }
      }
      if (type === 'is_partner') {
        return { icon: 'handshake', color: 'deep-orange', label: 'Инициатор: подрядчик' }
      }
      return { icon: 'help_outline', color: 'grey-6', label: 'Инициатор: не определено' }
    },
    async loadData() {
      this.isLoading = true;
      const res = await getContractorAgreementList();
      this.isLoading = false;
      if (!res) return;
      this.rowsData = res;
    },
    async loadDataSilently() {
      const res = await getContractorAgreementList();
      if (res) {
        this.rowsData = res;
      }
    },
    async loadOptions() {
      this.isLoadingOptions = true;
      await Promise.all([
        this.loadContractors(),
        this.loadProviders(),
        this.loadSbsProjects()
      ]);
      this.isLoadingOptions = false;
    },
    async loadSbsProjects() {
      this.isLoadingSbsProjects = true;
      try {
        const res = await contractorAgreementListAPI.getSbsProjects();
        if (res) {
          this.sbsProjects = res.data || [];
        }
      } catch (e) {
        console.error('Error loading SBS projects:', e);
        this.sbsProjects = [];
      } finally {
        this.isLoadingSbsProjects = false;
      }
    },
    async loadContractors() {
      const res = await getContractors();
      if (res) {
        this.contractors = res;
      }
    },
    getActionsDropdown(row) {
      // Используем поля из БД вместо локальной логики
      const canEdit = row?.can_edit === true;
      const canDelete = row?.can_delete === true;
      const canSign = row?.can_sign === true;
      const canDownload = row?.can_download_agreement === true;
      const isFullySigned = row?.is_fully_signed === true;
      const isPartiallySigned = row?.is_partially_signed === true;
      const canSendTo1C = row?.can_sent_to_1c === true;
      const isSentTo1C = row?.is_sent_to_1c === 1 || row?.is_sent_to_1c === true;
      const contractorSigned = row?.contractor_sign_status === 'SIGNED';
      const contractorWaiting = row?.contractor_sign_status === 'WAITING';
      const isFromBinect = row?.is_from_binect === true;
      const hasRequests = row?.created_request_cnt > 0;

      // Текст для редактирования
      let editHint = '';
      if (!canEdit) {
        if (hasRequests) {
          editHint = 'Редактирование запрещено: по договору уже созданы заявки';
        } else if (isPartiallySigned) {
          editHint = 'Редактирование запрещено: договор частично подписан';
        } else if (isFullySigned) {
          editHint = 'Редактирование запрещено: договор полностью подписан';
        } else if (isSentTo1C) {
          editHint = 'Редактирование запрещено: договор уже отправлен в 1С';
        } else {
          editHint = 'Редактирование недоступно';
        }
      }

      // Текст для удаления
      let deleteHint = '';
      if (!canDelete) {
        if (hasRequests) {
          deleteHint = 'Удаление запрещено: по договору уже созданы заявки';
        } else if (isPartiallySigned) {
          deleteHint = 'Удаление запрещено: договор частично подписан';
        } else if (isFullySigned) {
          deleteHint = 'Удаление запрещено: договор полностью подписан';
        } else if (isSentTo1C) {
          deleteHint = 'Удаление запрещено: договор уже отправлен в 1С';
        } else {
          deleteHint = 'Удаление недоступно';
        }
      }

      // Текст для подписания
      let signHint = '';
      if (contractorSigned) {
        signHint = 'Застройщик уже подписал договор';
      } else if (isFromBinect) {
        signHint = 'Подписание недоступно: договор подписан в системе BINECT';
      } else if (!canSign) {
        signHint = row?.next_sign_description || 'Подписание недоступно';
      } else if (contractorWaiting && row?.contractor_sign_expired === false) {
        signHint = 'Продолжить подписание';
      } else if (contractorWaiting && row?.contractor_sign_expired === true) {
        signHint = 'Ссылка истекла. Создать новую';
      }

      // Текст для отправки в 1С
      let sendTo1CHint = '';
      if (isSentTo1C) {
        sendTo1CHint = 'Договор уже отправлен в 1С';
      } else if (!canSendTo1C) {
        if (isFromBinect) {
          sendTo1CHint = 'Отправка в 1С недоступна: договор из BINECT';
        } else if (isPartiallySigned) {
          sendTo1CHint = 'Отправка в 1С недоступна: договор должен быть полностью подписан обеими сторонами';
        } else {
          sendTo1CHint = 'Отправка в 1С недоступна: договор должен быть полностью подписан застройщиком и поставщиком';
        }
      }

      return [
        {
          label: 'Редактировать',
          action: 'edit',
          icon: 'edit',
          group: 'actions',
          disabled: !canEdit,
          hint: editHint
        },
        {
          label: 'Скачать договор',
          action: 'download',
          icon: 'download',
          group: 'actions',
          disabled: !canDownload,
          hint: !canDownload ? (isFromBinect ? 'Скачивание недоступно: договор из BINECT' : 'Скачивание недоступно: документ не загружен в хранилище') : ''
        },
        {
          label: contractorSigned ? 'Подписан' : (contractorWaiting ? 'Продолжить' : 'Подписать'),
          action: 'sign',
          icon: 'verified',
          group: 'actions',
          disabled: !canSign || contractorSigned,
          hint: signHint
        },
        {
          label: 'Отправить в 1С',
          action: 'send_to_1c',
          icon: 'send',
          group: 'actions',
          disabled: !canSendTo1C || isSentTo1C,
          hint: sendTo1CHint
        },
        {
          label: 'Удалить',
          action: 'delete',
          icon: 'delete',
          group: 'delete',
          color: 'negative',
          disabled: !canDelete,
          hint: deleteHint
        },
      ];
    },
    getGroupLabels(group) {
      if (group === 'actions') return 'Действия'
      if (group === 'delete') return ''
      return group
    },
    async handleAction(action, row) {
      if (action?.disabled) return;

      const id = row.contractor_agreement_list_id;
      
      switch (action.action) {
        case 'edit':
          this.openDialog(row);
          break;
        case 'delete':
          this.confirmDelete(row);
          break;
        case 'download':
          this.actionLoading[id] = true;
          try {
            await this.downloadAgreement(row);
          } finally {
            this.actionLoading[id] = false;
          }
          break;
        case 'sign':
          this.actionLoading[id] = true;
          try {
            await this.signAgreement(row);
          } finally {
            this.actionLoading[id] = false;
          }
          break;
        case 'send_to_1c':
          this.actionLoading[id] = true;
          try {
            await this.sendTo1C(row);
          } finally {
            this.actionLoading[id] = false;
          }
          break;
      }
    },
    async downloadAgreement(row) {
      try {
        // Всегда скачиваем через бэкенд, который сам определит,
        // есть ли подписанный документ или нужно конвертировать DOCX
        const response = await contractorAgreementListAPI.downloadAgreement(row.contractor_agreement_list_id);
        const { saveAs } = await import('file-saver');
        const blob = new Blob([response.data], {
          type: 'application/pdf'
        });

        const agreementNum = row.agreement_num || row.contractor_agreement_list_id;
        const filename = `Договор поставки №${agreementNum}.pdf`;
        saveAs(blob, filename);
        this.showSucceedSnackbar('Договор успешно скачан');
      } catch (e) {
        console.error('Error downloading agreement:', e);
        this.showErrorSnackbar('Ошибка при скачивании договора');
      }
    },
    async signAgreement(row) {
      // Используем поля can_sign из БД
      if (!row?.can_sign) {
        this.showErrorSnackbar(row?.next_sign_description || 'Подписание сейчас недоступно');
        return;
      }

      if (row?.contractor_sign_status === 'SIGNED') {
        this.showSucceedSnackbar('Договор уже подписан');
        return;
      }

      try {
        const res = await contractorAgreementListAPI.sign(row.contractor_agreement_list_id);
        const signUrl = res?.sign_url;

        if (!signUrl) {
          this.showErrorSnackbar('Сервис подписи не вернул ссылку для подписания');
          return;
        }

        // Переход на страницу подписания NCALayer
        window.location.href = signUrl;
      } catch (e) {
        console.error('Error creating sign process:', e);
        this.showErrorSnackbar('Ошибка при создании процесса подписания');
      }
    },
    async sendTo1C(row) {
      // Используем поле can_sent_to_1c из БД
      if (!row?.can_sent_to_1c) {
        this.showErrorSnackbar('Отправка в 1С доступна только после полной подписи договора (застройщик и поставщик)');
        return;
      }
      try {
        await contractorAgreementListAPI.sendTo1C(row.contractor_agreement_list_id);
        this.showSucceedSnackbar('Договор отправлен в 1С');
        // Обновляем данные из БД без loader (реактивное обновление)
        await this.loadDataSilently();
      } catch (e) {
        console.error('Error sending to 1C:', e);
        this.showErrorSnackbar('Ошибка при отправке договора в 1С');
      }
    },
    async loadProviders() {
      const res = await getProviders();
      if (res) {
        this.providers = res;
      }
    },
    async openDialog(row = null) {
      this.editing = row;
      this.sbsAgreements = []; // Сбрасываем список договоров
      
      // Открываем диалог сразу чтобы показать loader
      this.dialog = true;
      
      try {
        // Загружаем данные (всегда свежие, без кэша)
        await this.loadOptions();
        
        if (row) {
          let documentDate = '';
          if (row.document_date) {
            // Преобразуем дату из формата YYYY-MM-DD в DD.MM.YYYY для компонента CustomDate
            try {
              const date = new Date(row.document_date);
              if (!isNaN(date.getTime())) {
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                documentDate = `${day}.${month}.${year}`;
              }
            } catch (e) {
              documentDate = row.document_date;
            }
          }

          let dateEnd = '';
          if (row.date_end) {
            // Преобразуем дату из формата YYYY-MM-DD в DD.MM.YYYY для компонента CustomDate
            try {
              const date = new Date(row.date_end);
              if (!isNaN(date.getTime())) {
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                dateEnd = `${day}.${month}.${year}`;
              }
            } catch (e) {
              dateEnd = row.date_end;
            }
          }
          
          this.form = {
            contractor_agreement_list_id: row.contractor_agreement_list_id,
            contractor_id: row.contractor_id,
            provider_id: row.provider_id,
            sbs_project_id: row.sbs_project_id,
            document_date: documentDate,
            date_end: dateEnd,
            is_from_binect: row.is_from_binect || false,
            agreement_num: row.agreement_num || '',
            guid: row.guid || '',
            sbs_agreement_guid: row.guid || null,
            sbs_dogovor_kontragenta_id: row.sbs_dogovor_kontragenta_id || null
          };
          
          // Явно вызываем загрузку договоров если все условия выполнены
          await this.$nextTick();
          if (this.form.is_from_binect && this.form.contractor_id && this.form.provider_id && this.form.sbs_project_id) {
            await this.loadSbsAgreements();
          }
          
        } else {
          this.form = {
            contractor_agreement_list_id: null,
            contractor_id: null,
            provider_id: null,
            sbs_project_id: null,
            document_date: '',
            date_end: '',
            is_from_binect: false,
            agreement_num: '',
            guid: '',
            sbs_agreement_guid: null,
            sbs_dogovor_kontragenta_id: null
          };
        }
      } catch (e) {
        console.error('Error opening dialog:', e);
        this.showErrorSnackbar('Ошибка при загрузке данных');
        this.dialog = false; // Закрываем диалог при ошибке
      }
    },
    async save() {
      this.isSaving = true;
      const data = {
        contractor_agreement_list_id: this.editing ? this.editing.contractor_agreement_list_id : null,
        contractor_id: this.form.contractor_id,
        provider_id: this.form.provider_id,
        sbs_project_id: this.form.sbs_project_id,
        document_date: this.form.document_date || null,
        date_end: this.form.date_end || null,
        is_from_binect: this.form.is_from_binect || false,
        agreement_num: this.form.is_from_binect ? this.form.agreement_num : null,
        guid: this.form.is_from_binect ? this.form.guid : null,
        sbs_dogovor_kontragenta_id: this.form.is_from_binect ? this.form.sbs_dogovor_kontragenta_id : null
      };

      const res = await saveContractorAgreementList(data);
      this.isSaving = false;

      if (res) {
        this.showSucceedSnackbar(this.editing ? 'Договор обновлен' : 'Договор добавлен');
        this.dialog = false;
        
        // Обновляем данные из БД без loader (реактивное обновление)
        await this.loadDataSilently();
      }
    },
    confirmDelete(row) {
      this.confirmDialog = {
        show: true,
        title: 'Подтверждение',
        message: `Вы уверены, что хотите удалить договор с ID ${row.contractor_agreement_list_id}?`,
        onConfirm: async () => {
          await this.deleteHandler(row.contractor_agreement_list_id);
        }
      };
    },
    async deleteHandler(contractor_agreement_list_id) {
      const res = await deleteContractorAgreementList(contractor_agreement_list_id);
      if (res) {
        this.showSucceedSnackbar('Договор удален');
        
        // Обновляем данные из БД без loader (реактивное обновление)
        await this.loadDataSilently();
      }
    },
    async onConfirmDelete() {
      this.confirmDialog.show = false;
      if (this.confirmDialog.onConfirm) {
        await this.confirmDialog.onConfirm();
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('ru-RU');
      } catch (e) {
        return dateStr;
      }
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        return date.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
      } catch (e) {
        return dateStr;
      }
    },
    getSideSignLabel(status) {
      if (status === 'SIGNED') return 'Подписан';
      if (status === 'WAITING') return 'На подписи';
      if (!status) return 'Не подписан';
      return status;
    },
    getSignIcon(status) {
      if (status === 'SIGNED') {
        return { icon: 'check_circle', color: 'positive' };
      }
      if (status === 'WAITING') {
        return { icon: 'hourglass_empty', color: 'warning' };
      }
      return { icon: 'cancel', color: 'grey-5' };
    },
    getOverallSignStatus(row) {
      // Используем поля из БД
      const isFully = row?.is_fully_signed === true;
      const isPartially = row?.is_partially_signed === true;
      const canSign = row?.can_sign === true;

      if (isFully) {
        return { label: 'Подписан полностью', color: 'positive' };
      }

      if (isPartially) {
        return { label: 'Подписан с одной стороны', color: 'warning' };
      }

      if (canSign) {
        return { label: 'На подписи', color: 'warning' };
      }

      return null;
    },
    async checkTemplateAccess() {
      try {
        const res = await supplyAgreementTemplateAPI.checkAccess();
        this.canManageTemplates = res?.has_access === true;
      } catch (e) {
        this.canManageTemplates = false;
      }
    },
    async loadSbsAgreements() {
      // Загружаем договоры из SBS только если выбраны все необходимые данные
      const contractor = this.selectedContractor;
      const provider = this.selectedProvider;
      const project = this.selectedSbsProject;

      if (!contractor || !provider || !project) {
        this.sbsAgreements = [];
        return;
      }

      // Проверяем, что у нас есть все необходимые GUID
      if (!contractor.is_builder_company) {
        this.sbsAgreements = [];
        return;
      }

      if (!contractor.sbs_company_guid) {
        this.showWarningSnackbar('У выбранного подрядчика не указан GUID компании в SBS');
        this.sbsAgreements = [];
        return;
      }

      if (!provider.sbs_partner_guid) {
        this.showWarningSnackbar('У выбранного поставщика не указан GUID партнера в SBS');
        this.sbsAgreements = [];
        return;
      }

      if (!project.sbs_project_guid) {
        this.showWarningSnackbar('У выбранного проекта не указан GUID в SBS');
        this.sbsAgreements = [];
        return;
      }

      this.isLoadingSbsAgreements = true;
      try {
        const res = await contractorAgreementListAPI.getSbsAgreements(
          contractor.sbs_company_guid,
          project.sbs_project_guid,
          provider.sbs_partner_guid
        );
        
        if (res && res.data) {
          this.sbsAgreements = res.data;
          
          if (this.sbsAgreements.length === 0) {
            this.showWarningSnackbar('Договоры не найдены в справочнике SBS для выбранной комбинации');
          }
        } else {
          this.sbsAgreements = [];
        }
      } catch (e) {
        console.error('Error loading SBS agreements:', e);
        this.showErrorSnackbar('Ошибка при загрузке договоров из SBS');
        this.sbsAgreements = [];
      } finally {
        this.isLoadingSbsAgreements = false;
      }
    },
    handleSbsAgreementChange(guid) {
      this.form.sbs_agreement_guid = guid;
      
      // Автоматически заполняем номер договора, GUID, дату и ID
      const agreement = this.sbsAgreements.find(a => a.guid === guid);
      if (agreement) {
        this.form.agreement_num = agreement.nomer_dogovora;
        this.form.guid = agreement.guid;
        this.form.document_date = agreement.data_dogovora || '';
        this.form.sbs_dogovor_kontragenta_id = agreement.sbs_dogovor_kontragenta_id;
      } else {
        this.form.document_date = '';
        this.form.sbs_dogovor_kontragenta_id = null;
      }
    },
  },
  watch: {
    'form.contractor_id'() {
      // Вызываем только если все три параметра выбраны И галочка "другая система" ВКЛЮЧЕНА
      if (this.form.contractor_id && this.form.provider_id && this.form.sbs_project_id && this.form.is_from_binect) {
        this.loadSbsAgreements();
      } else {
        this.sbsAgreements = [];
      }
    },
    'form.provider_id'() {
      // Вызываем только если все три параметра выбраны И галочка "другая система" ВКЛЮЧЕНА
      if (this.form.contractor_id && this.form.provider_id && this.form.sbs_project_id && this.form.is_from_binect) {
        this.loadSbsAgreements();
      } else {
        this.sbsAgreements = [];
      }
    },
    'form.sbs_project_id'() {
      // Вызываем только если все три параметра выбраны И галочка "другая система" ВКЛЮЧЕНА
      if (this.form.contractor_id && this.form.provider_id && this.form.sbs_project_id && this.form.is_from_binect) {
        this.loadSbsAgreements();
      } else {
        this.sbsAgreements = [];
      }
    },
    'form.is_from_binect'(newVal) {
      if (newVal) {
        // Если включаем галочку - проверяем, можем ли загрузить договоры
        if (this.form.contractor_id && this.form.provider_id && this.form.sbs_project_id) {
          this.loadSbsAgreements();
        }
      } else {
        // Если выключаем режим "другой системы", очищаем договор из SBS
        this.form.sbs_agreement_guid = null;
        this.sbsAgreements = [];
      }
    },
  },
  async mounted() {
    this.loadData();
    this.checkTemplateAccess();
    const query = this.$route.query;
    if (query.add === '1') {
      await this.openDialog();
      if (query.sbs_project_id) this.form.sbs_project_id = Number(query.sbs_project_id);
    }
  },
  setup() {
    const { showSucceedSnackbar, showErrorSnackbar, showWarningSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar, showErrorSnackbar, showWarningSnackbar };
  },
};
</script>

<style scoped lang="scss">
.contractor-agreement-list-page {
  padding: 8px;
}

.initiator-filter {
  min-width: 180px;
}

.top-bar {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  :deep(.title) {
    margin-bottom: 0 !important;
  }
}

.header__create-btn {
  width: auto;
  height: 46px;
}

.header__create-btn-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff;
  color: #0060fe;
}

:deep(.q-btn__content) {
  display: flex;
  align-items: center;
  gap: 16px;
}

.action-buttons {
  display: flex;
  gap: 4px;
  justify-content: center;
}

.integration-status {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

.integration-status .status-icon {
  display: flex;
  align-items: center;
  text-decoration: none;
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
}

.sign-status-icons {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
}

.sign-status-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  width: 100%;
  padding: 4px 0;
}

.sign-status-row {
  display: flex;
  align-items: center;
  gap: 2px;
  position: relative;
  cursor: default;
}

.sign-next-step {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #1976d2;
  max-width: 180px;
}

.sign-next-step-text {
  text-align: center;
  line-height: 1.2;
}

.sign-binect-badge {
  margin-top: 2px;
}

/* Стили модального окна */
.agreement-modal {
  min-width: 500px;
  max-width: 700px;
}

.agreement-modal__header {
  padding: 12px 16px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
}

.agreement-modal__title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  flex-grow: 1;
}

.agreement-modal__close {
  color: #666;
}

.agreement-modal__content {
  padding: 16px;
  max-height: 70vh;
  overflow-y: auto;
}

.agreement-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.agreement-form__section {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.agreement-form__item {
  width: 100%;
}

.agreement-form__hint {
  margin-top: 6px;
  font-size: 12px;
  color: #666;
}

.agreement-form__binect-fields {
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 4px;
  margin-top: 10px;
}

.agreement-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #e0e0e0;
}

.agreement-modal__footer :deep(button) {
  width: auto;
  min-width: 100px;
  height: 36px;
}

.agreement-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  gap: 16px;
}

.agreement-loading__text {
  font-size: 14px;
  color: #666;
}

.sign-info {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &__badge {
    width: fit-content;
  }

  &__details {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 12px;
    color: #333;
  }

  &__signer {
    display: flex;
    flex-direction: column;
  }

  &__date {
    color: #888;
    font-size: 11px;
  }
}

:deep(.q-tab-panels) {
  overflow: visible;
}

:deep(.q-tab-panel) {
  padding-top: 8px;
}

:deep(.q-tabs) {
  margin-bottom: 0;
}

@media (max-width: 768px) {
}
</style>

